import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';

/**
 * @typedef {object} VariantConfiguratorImageData
 * @property {string} src - Bild-URL
 * @property {string} alt - Alt-Text
 */

/**
 * @typedef {object} VariantConfiguratorImages
 * @property {Object.<string, VariantConfiguratorImageData>} step1_size - Bilder für Schritt 1
 * @property {Object.<string, VariantConfiguratorImageData>} step2_bottle - Bilder für Schritt 2
 * @property {Object.<string, string>} fallback - Fallback-Bilder
 */

/**
 * @typedef {object} VariantConfiguratorRefs
 * @property {HTMLSelectElement[]} selects - Die Select-Elemente für die 3 Schritte
 * @property {HTMLElement} imageContainer - Container für die Produktbilder
 */

/**
 * Ein Custom Element für einen 3-Schritte Varianten-Konfigurator.
 * Zeigt schrittweise Dropdowns für Produktoptionen und lädt passende Bilder.
 * 
 * @extends {Component<VariantConfiguratorRefs>}
 */
export default class VariantConfiguratorComponent extends Component {
  /** @type {Array<Object>} */
  #variants = [];

  /** @type {Array<Object>} */
  #options = [];

  /** @type {Object | null} */
  #selectedVariant = null;

  /** @type {string[]} */
  #selectedValues = ['', '', ''];

  /** @type {HTMLSelectElement[]} */
  #selects = [];

  /** @type {string} */
  #originalImageSrc = '';

  connectedCallback() {
    super.connectedCallback();

    // Lade Varianten-Daten aus dem JSON Script
    this.#loadVariantData();

    // Finde alle Select-Elemente
    this.#selects = Array.from(this.querySelectorAll('.variant-configurator__select'));

    // Event Listener für alle Dropdowns
    this.#selects.forEach((select, index) => {
      select.addEventListener('change', () => this.#handleStepChange(index));
    });

    // Wenn User von Collection kommt → ignoriere ?variant Parameter
    const comeFromCollection = document.referrer.includes('/collections/');


    // Wenn eine Variante bereits ausgewählt ist (z.B. via URL-Parameter), setze die Dropdowns
    if (this.#selectedVariant && !comeFromCollection) {
      this.#initializeFromSelectedVariant();
    }

    // Initial-Status setzen
    this.#updateAvailableOptions();
  }

  /**
   * Lädt die Varianten-Daten aus dem JSON Script-Tag
   */
  #loadVariantData() {
    const dataScript = this.querySelector('script[data-variant-data]');
    if (!dataScript) return;

    try {
      const data = JSON.parse(dataScript.textContent || '{}');
      this.#variants = data.variants || [];
      this.#options = data.options || [];
      this.#selectedVariant = data.selectedVariant || null;
    } catch (error) {
      console.error('Fehler beim Laden der Varianten-Daten:', error);
    }

    // Speichere originales Bild der Media Gallery
    this.#saveOriginalImage();
  }

  /**
   * Speichert das Original-Bild der Media Gallery
   */
  #saveOriginalImage() {
    const mediaGallery = document.querySelector('.media-gallery');
    if (mediaGallery) {
      const firstImage = mediaGallery.querySelector('img');
      if (firstImage instanceof HTMLImageElement) {
        this.#originalImageSrc = firstImage.src;
      }
    }
  }

  /**
   * Initialisiert die Dropdowns basierend auf der bereits ausgewählten Variante
   * (z.B. wenn die Seite mit ?variant=ID geladen wird)
   */
  #initializeFromSelectedVariant() {
    if (!this.#selectedVariant) return;

    const variantAny = /** @type {any} */ (this.#selectedVariant);
    
    // Setze die selected values basierend auf der Variante
    this.#selectedValues[0] = variantAny.option1 || '';
    this.#selectedValues[1] = variantAny.option2 || '';
    this.#selectedValues[2] = variantAny.option3 || '';

    // Setze die Dropdown-Werte
    if (this.#selects[0] && this.#selectedValues[0]) {
      this.#selects[0].value = this.#selectedValues[0];
      this.#selects[0].disabled = false;
    }

    if (this.#selects[1] && this.#selectedValues[1]) {
      this.#selects[1].value = this.#selectedValues[1];
      this.#selects[1].disabled = false;
    }

    if (this.#selects[2] && this.#selectedValues[2]) {
      this.#selects[2].value = this.#selectedValues[2];
      this.#selects[2].disabled = false;
    }
  }
  
  /**
   * Aktualisiert das Bild in der Media Gallery
   * @param {string} imageUrl - Die neue Bild-URL
   * @param {string} altText - Der Alt-Text für das Bild
   */
  #updateGalleryImage(imageUrl, altText) {
    const selector = this.dataset.gallerySelector || '.product-information__media';
    const container = document.querySelector(selector);
    if (!container) return;

    // Finde das erste Bild in der Gallery
    const firstImage = container.querySelector('.media-gallery__grid img');
    if (firstImage instanceof HTMLImageElement) {
      firstImage.src = imageUrl;
      firstImage.alt = altText;
      
      // Aktualisiere auch srcset wenn vorhanden
      if (firstImage.srcset) {
        firstImage.srcset = imageUrl;
      }
    }
  }

  /**
   * Setzt das Original-Bild der Gallery wieder zurück
   */
  #restoreOriginalImage() {
    if (!this.#originalImageSrc) return;
    
    const selector = this.dataset.gallerySelector || '.product-information__media';
    const container = document.querySelector(selector);
    if (!container) return;

    const firstImage = container.querySelector('.media-gallery__grid img');
    if (firstImage instanceof HTMLImageElement) {
      firstImage.src = this.#originalImageSrc;
    }
  }

  /**
   * Behandelt Änderungen in einem Dropdown-Schritt
   * @param {number} stepIndex - Index des geänderten Schritts (0, 1, oder 2)
   */
  #handleStepChange(stepIndex) {
    const select = this.#selects[stepIndex];
    if (!select) return;

    // Speichere den gewählten Wert
    this.#selectedValues[stepIndex] = select.value;

    // Setze nachfolgende Schritte zurück
    for (let i = stepIndex + 1; i < 3; i++) {
      this.#selectedValues[i] = '';
      const nextSelect = this.#selects[i];
      if (nextSelect) {
        nextSelect.value = '';
        nextSelect.disabled = true;
      }
    }

    // Aktualisiere verfügbare Optionen für nachfolgende Schritte
    this.#updateAvailableOptions();

    // Prüfe ob eine komplette Variante ausgewählt wurde
    if (this.#selectedValues.every(val => val !== '')) {
      this.#selectVariant();
      // Schritt 3: Shopify's normale Varianten-Logik übernimmt die Bild-Aktualisierung
    } else {
      // Zeige entsprechendes Bild je nach Schritt
      if (stepIndex === 0 && this.#selectedValues[0]) {
        // Schritt 1: Zeige Basis-Bild (nur Größe)
        this.#showStep1Image(this.#selectedValues[0]);
      } else if (stepIndex === 1 && this.#selectedValues[0] && this.#selectedValues[1]) {
        // Schritt 2: Zeige Bild mit Größe + Flasche (mit erstem verfügbaren Verschluss)
        this.#showStep2Image(this.#selectedValues[0], this.#selectedValues[1]);
      }
    }
  }

  /**
   * Aktualisiert die verfügbaren Optionen für die Dropdowns
   */
  #updateAvailableOptions() {
    // Für jeden Schritt die verfügbaren Optionen filtern
    for (let stepIndex = 0; stepIndex < 3; stepIndex++) {
      const select = this.#selects[stepIndex];
      if (!select) continue;

      // Schritt 1 ist immer aktiv
      if (stepIndex === 0) {
        select.disabled = false;
        continue;
      }

      // Prüfe ob alle vorherigen Schritte ausgefüllt sind
      const previousStepsFilled = this.#selectedValues
        .slice(0, stepIndex)
        .every(val => val !== '');

      if (!previousStepsFilled) {
        select.disabled = true;
        continue;
      }

      // Finde verfügbare Optionen basierend auf den vorherigen Auswahlen
      const availableOptions = this.#getAvailableOptionsForStep(stepIndex);

      // Aktualisiere das Select-Element
      this.#updateSelectOptions(select, availableOptions, stepIndex);
      select.disabled = false;
    }
  }

  /**
   * Findet verfügbare Optionswerte für einen bestimmten Schritt
   * @param {number} stepIndex - Index des Schritts
   * @returns {string[]} Array der verfügbaren Optionswerte
   */
  #getAvailableOptionsForStep(stepIndex) {
    const availableOptions = new Set();

    // Filtere Varianten basierend auf den bisherigen Auswahlen
    const matchingVariants = this.#variants.filter(variant => {
      for (let i = 0; i < stepIndex; i++) {
        if ((/** @type {any} */ (variant))[`option${i + 1}`] !== this.#selectedValues[i]) {
          return false;
        }
      }
      return true;
    });

    // Sammle alle verfügbaren Werte für diesen Schritt
    matchingVariants.forEach(variant => {
      const optionValue = (/** @type {any} */ (variant))[`option${stepIndex + 1}`];
      if (optionValue) {
        availableOptions.add(optionValue);
      }
    });

    return Array.from(availableOptions);
  }

  /**
   * Aktualisiert die Optionen eines Select-Elements
   * @param {HTMLSelectElement} select - Das Select-Element
   * @param {string[]} availableOptions - Verfügbare Optionswerte
   * @param {number} stepIndex - Index des Schritts
   */
  #updateSelectOptions(select, availableOptions, stepIndex) {
    // Speichere den aktuell gewählten Wert
    const currentValue = this.#selectedValues[stepIndex];
    const placeholderText = select.options[0]?.textContent || '';
    
    // Leere alle Optionen
    select.innerHTML = '';

    // Füge Placeholder zurück
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholderText;
    placeholderOption.disabled = true;
    select.appendChild(placeholderOption);

    // Füge verfügbare Optionen hinzu
    availableOptions.forEach(optionValue => {
      const option = document.createElement('option');
      option.value = optionValue;
      option.textContent = optionValue;
      select.appendChild(option);
    });
    
    // Setze den Wert: entweder der bereits gewählte oder Placeholder
    if (currentValue && availableOptions.includes(currentValue)) {
      select.value = currentValue;
    } else {
      // Setze auf Placeholder
      select.value = '';
    }
  }

  /**
   * Wählt die passende Variante basierend auf den 3 gewählten Optionen
   */
  #selectVariant() {
    const variant = this.#variants.find(v => {
      const vAny = /** @type {any} */ (v);
      return vAny.option1 === this.#selectedValues[0] &&
        vAny.option2 === this.#selectedValues[1] &&
        vAny.option3 === this.#selectedValues[2];
    });

    if (!variant) {
      console.warn('Keine passende Variante gefunden für:', this.#selectedValues);
      return;
    }

    this.#selectedVariant = variant;

    const variantAny = /** @type {any} */ (variant);

    // Feuere VariantSelectedEvent
    const selectedEvent = new VariantSelectedEvent({ id: String(variantAny.id) });
    this.dispatchEvent(selectedEvent);

    // Feuere VariantUpdateEvent mit den Daten
    const updateEvent = new VariantUpdateEvent(
      {
        id: variantAny.id,
        available: variantAny.available,
        inventory_management: variantAny.inventory_management,
        featured_media: variantAny.featured_media,
      },
      this.dataset.blockId || '',
      {
        html: document,
        productId: this.dataset.productId || '',
      }
    );
    this.dispatchEvent(updateEvent);

    // Aktualisiere die URL mit der Varianten-ID
    this.#updateURL(variantAny.id);

    // Aktualisiere das Bild wenn die Variante ein featured_media hat
    if (variantAny.featured_media && variantAny.featured_media.preview_image) {
      const imageUrl = variantAny.featured_media.preview_image.src;
      const altText = variantAny.featured_media.alt || variantAny.name || 'Produktbild';
      this.#updateGalleryImage(imageUrl, altText);
    }
  }

  /**
   * Aktualisiert die URL mit der ausgewählten Varianten-ID
   * @param {number} variantId - Die ID der Variante
   */
  #updateURL(variantId) {
    const url = new URL(window.location.href);
    
    if (variantId) {
      url.searchParams.set('variant', String(variantId));
    } else {
      url.searchParams.delete('variant');
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Zeigt das Basis-Bild für Schritt 1 (nur Größe)
   * @param {string} option1 - Wert der ersten Option
   */
  #showStep1Image(option1) {
    // @ts-ignore - window.variantConfiguratorImages wird von Liquid Snippet gesetzt
    if (!window.variantConfiguratorImages) return;

    // @ts-ignore
    const imageData = window.variantConfiguratorImages.step1_size[option1];
    if (imageData) {
      this.#updateGalleryImage(imageData.src, imageData.alt);
    }
  }

  /**
   * Zeigt das Bild für Schritt 2 (Größe + Flasche mit erstem verfügbaren Verschluss)
   * @param {string} option1 - Wert der ersten Option (Größe)
   * @param {string} option2 - Wert der zweiten Option (Flasche)
   */
  #showStep2Image(option1, option2) {
    // @ts-ignore - window.variantConfiguratorImages wird von Liquid Snippet gesetzt
    if (!window.variantConfiguratorImages) return;

    const key = `${option1}_${option2}`;
    // @ts-ignore
    const imageData = window.variantConfiguratorImages.step2_bottle[key];
    
    if (imageData) {
      this.#updateGalleryImage(imageData.src, imageData.alt);
    } else {
      // @ts-ignore
      const fallback = window.variantConfiguratorImages.fallback;
      if (fallback && fallback.noImage) {
        this.#updateGalleryImage(fallback.noImage, 'Bild nicht verfügbar');
      }
    }
  }



  /**
   * Erstellt einen Slug aus einem String (wie Liquid's handleize Filter)
   * @param {string} str - Der zu konvertierende String
   * @returns {string} Der Slug
   */
  #slugify(str) {
    return str
      .toLowerCase()
      .replace(/[äöü]/g, match => ({ 'ä': 'a', 'ö': 'o', 'ü': 'u' }[match] || match))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }



  /**
   * Gibt die aktuell ausgewählte Variante zurück
   * @returns {Object | null}
   */
  get selectedVariant() {
    return this.#selectedVariant;
  }

  /**
   * Gibt die aktuell gewählten Werte zurück
   * @returns {string[]}
   */
  get selectedValues() {
    return [...this.#selectedValues];
  }
}

// Registriere das Custom Element
if (!customElements.get('variant-configurator-component')) {
  customElements.define('variant-configurator-component', VariantConfiguratorComponent);
}
